import { useEffect } from "react";
import { X, Phone } from "lucide-react";
import { trackEvent } from "../lib/analytics";

/**
 * Premium glassmorphism modal for an individual team member.
 * - ESC closes
 * - Click outside closes
 * - Locks page scroll while open
 * - Smooth fade+scale entrance via Tailwind classes
 */
const TeamMemberModal = ({ member, onClose }) => {
  // Lock body scroll + handle ESC
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  if (!member) return null;

  const initials =
    member.initials ||
    member.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const fullBio = member.full_bio?.trim() || member.bio?.trim() || "";

  return (
    <div
      data-testid="team-member-modal"
      onClick={onClose}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-[#1c1c1e]/55 p-3 backdrop-blur-md animate-modal-fade md:p-6"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${member.name} · ${member.role}`}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl animate-modal-pop overflow-hidden rounded-2xl border border-white/60 bg-white/95 shadow-2xl backdrop-blur-xl max-h-[90vh] flex flex-col"
      >
        {/* Close */}
        <button
          onClick={onClose}
          data-testid="team-modal-close"
          aria-label="Close"
          className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/95 text-[#1c1c1e] shadow-lux transition-all hover:scale-110 hover:bg-white"
        >
          <X size={16} />
        </button>

        <div className="grid grid-cols-1 md:grid-cols-12">
          {/* Profile column */}
          <div className="relative flex flex-col items-center justify-center bg-gradient-to-br from-[#1c1c1e] via-[#2a2a2c] to-[#1c1c1e] p-10 text-white md:col-span-5">
            <div className="absolute inset-0 bg-grid opacity-10" />
            <div className="absolute -bottom-20 -right-20 h-64 w-64 rounded-full bg-[#e85a1c]/30 blur-3xl" />
            <div className="relative">
              {member.image_url ? (
                <img
                  src={member.image_url}
                  alt={member.name}
                  data-testid="team-modal-image"
                  className="h-44 w-44 rounded-full object-cover shadow-2xl ring-4 ring-white/20"
                />
              ) : (
                <div
                  data-testid="team-modal-initials"
                  className="flex h-44 w-44 items-center justify-center rounded-full bg-gradient-to-br from-[#e85a1c] to-[#c64812] font-display text-7xl tracking-tighter text-white shadow-2xl ring-4 ring-white/15"
                >
                  {initials}
                </div>
              )}
            </div>

            <div className="relative mt-7 text-center">
              <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#ffaa7a]">
                / Leadership
              </div>
              <div
                className="mt-2 font-display text-2xl uppercase leading-tight tracking-tight"
                data-testid="team-modal-name"
              >
                {member.name}
              </div>
              <div
                className="mt-1 font-mono-industrial text-[11px] uppercase tracking-[0.25em] text-white/70"
                data-testid="team-modal-role"
              >
                {member.role}
              </div>
            </div>
          </div>

          {/* Detail column */}
          <div className="overflow-y-auto p-7 md:col-span-7 md:p-10">
            <div className="font-mono-industrial text-[10px] uppercase tracking-[0.3em] text-[#e85a1c]">
              / About the role
            </div>
            <h3 className="mt-3 font-display text-2xl uppercase leading-[0.95] tracking-tight text-[#1c1c1e] md:text-3xl">
              {member.bio || member.role}
            </h3>

            {fullBio && fullBio !== member.bio && (
              <div className="mt-6 space-y-4 text-[15px] leading-[1.75] text-[#4a4a4e]">
                {fullBio.split(/\n\s*\n/).map((para, i) => (
                  <p key={para.slice(0, 24) || `bio-${i}`} data-testid={`team-modal-bio-${i}`}>
                    {para}
                  </p>
                ))}
              </div>
            )}

            <div className="mt-8 flex flex-wrap gap-3 border-t border-black/5 pt-6">
              <a
                href="tel:+919533561060"
                data-testid="team-modal-contact"
                onClick={() => trackEvent("phone_click", { source: "team_modal", member: member?.name, phone_number: "+91 95335 61060" })}
                className="inline-flex items-center gap-2 rounded-full bg-[#e85a1c] px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-all hover:bg-[#c64812]"
              >
                <Phone size={13} /> Contact Office
              </a>
              <button
                onClick={onClose}
                className="inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#4a4a4e] transition-all hover:border-[#e85a1c] hover:text-[#e85a1c]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMemberModal;
